import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  IconButton,
  CircularProgress,
  Modal,
  TextField,
  Button,
  Badge,
  FormHelperText,
  FormControl,
  MenuItem
} from '@material-ui/core';
import SecurityRoundedIcon from '@material-ui/icons/SecurityRounded';
import MoreHorizRoundedIcon from '@material-ui/icons/MoreHorizRounded';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import EditRoundedIcon from '@material-ui/icons/EditRounded';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import { selectToken } from '../features/externalState/externalSlice';
import { isDocuSignApp, DocuSignDigitalReferralButton } from './DocuSignDigitalReferral';
import { fetchAllCredentialsForApp } from '../features/credentials/fetchCredentialsForApp';

// put near other imports / top-level helpers
const GOOGLE_APPS = ['googlesheets','googledocs','googleforms','googleslides','googledrive'];

const MIME_TYPES_BY_APP = {
  googledocs: ['application/vnd.google-apps.document'],
  googlesheets: ['application/vnd.google-apps.spreadsheet'],
  googleslides: ['application/vnd.google-apps.presentation'],
  googleforms: ['application/vnd.google-apps.form'],
  googledrive: [ // allow folders + any file
    'application/vnd.google-apps.folder',
    // Optionally allow all file types by NOT restricting here, or add common Google file types:
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation',
    'application/vnd.google-apps.form',
  ],
};


const isGoogleApp = (app) => GOOGLE_APPS.includes(app);

// convenience: read selections from node
const getSelections = (node) => Array.isArray(node?.data?.pickerSelections) ? node.data.pickerSelections : [];


function getModalStyle() {
  const top = 30;
  const left = 30;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    width: '100%',
  },
  button2: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    justifyContent: 'center',
    width: '20%',
    backgroundColor: '#0069ea',
    borderRadius: '4px',
    border: 'none',
    height: '50px',
    cursor: 'pointer',
    textTransform: 'none'
  },
  button3: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    justifyContent: 'center',
    width: '100%',
    //backgroundColor: '#0069ea',
    borderRadius: '4px',
    border: '1px solid #0069ea',
    height: '50px',
    cursor: 'pointer',
    textTransform: 'none'
  },
  paper: {
    position: 'absolute',
    width: '30%',
    borderRadius: 5,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4, 8, 6),
  },
  badgeSuccess: {
    backgroundColor: '#4caf50',
    color: '#4caf50',
  },
  badgeFailure: {
    backgroundColor: '#f44336',
    color: '#f44336',
  },
}));

let pickerLoaded = false;
let gapiLoaded = false;

function loadGoogleAPIs(callback) {
  if (pickerLoaded && gapiLoaded) {
    callback();
    return;
  }

  // Load gapi
  const gapiScript = document.createElement('script');
  gapiScript.src = 'https://apis.google.com/js/api.js';
  gapiScript.onload = () => {
    window.gapi.load('client:auth2', () => {
      gapiLoaded = true;
      checkReady();
    });
  };
  document.body.appendChild(gapiScript);

  // Load Picker
  const pickerScript = document.createElement('script');
  pickerScript.src = 'https://apis.google.com/js/api.js?onload=onPickerApiLoad';
  window.onPickerApiLoad = () => {
    pickerLoaded = true;
    checkReady();
  };
  document.body.appendChild(pickerScript);

  function checkReady() {
    if (pickerLoaded && gapiLoaded) callback();
  }
}


export default function AuthSelect({
  app,
  applabel,
  icon,
  custombutton,
  node,
  user,
  tenant,
  value,
  sendData,
  saveNode,
  sendAuth,
  sendLoggedOutToAuthorizer,
  breakpoint,
}) {
  const classes = useStyles();
  const [auths, setAuths] = useState([]);
  const [authname, setAuthName] = useState(node.data.authname); // "Zoho Books - MyName#1" etc.
  const [credentialStatus, setCredentialStatus] = useState(''); // 'success', 'failure', or ''
  const [modalStyle] = useState(getModalStyle);
  const [openModalState, setOpenModalState] = useState(false);
  const [namechange, setNameChange] = useState('');
  const [reallyDelete, setReallyDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newAuthId, setNewAuthId] = useState(null); // track newly created credential ID
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [testLoading, setTestLoading] = useState(false)
  // inside component
  const [pickedItems, setPickedItems] = useState([]); 

  async function fetchCredential(credentialId) {
    const r = await axios.get(
      `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?id=${credentialId}`
    );
    return r.data?.[0] || null;
  }
  
  function isExpired(cred) {
    // store expiry_date as epoch ms (recommended) or RFC3339. Adjust if needed.
  return true
  }
  
  async function refreshTokenServerSide(credentialId) {
    // Your new backend endpoint (or your Lambda op)
    const r = await axios.post(
      `https://editor.workload.co:5000/googleRefresh`,
      { credential: credentialId }, {
        'Content-Type': 'application/json'
      }
    );
    return r.data; // { access_token, expiry_date }
  }
  
  /**
   * Returns a guaranteed-fresh access token for this credential.
   * - Uses existing token if not expired
   * - Otherwise calls the refresh endpoint
   * - Retries once on 401 from Google
   */
  async function getFreshAccessToken(credentialId, { retryOn401 = false } = {}) {
    let cred = await fetchCredential(credentialId);
    if (!cred) throw new Error('Credential not found');
  
    if (!cred.access_token || isExpired(cred)) {
      const { access_token } = await refreshTokenServerSide(credentialId);
      return access_token;
    }
    return cred.access_token;
  }
// NEW: replace pickedItems with server-listed items
const [itemsLoading, setItemsLoading] = useState(false);
const [accessibleItems, setAccessibleItems] = useState([]);

// Utility to build a Drive query from your MIME_TYPES_BY_APP mapping
const buildMimeTypeQuery = (app) => {
  const types = MIME_TYPES_BY_APP[app] || [];
  if (!types.length) return "trashed=false";
  const ors = types.map(mt => `mimeType='${mt}'`).join(" or ");
  return `trashed=false and (${ors})`;
};

// NEW: list ALL files the app can access under drive.file (filtered by app)
const listFilesForApp = async (credentialId, app) => {
  setItemsLoading(true);
  try {
    const accessToken = await getFreshAccessToken(credentialId);
    const paramsBase = {
      fields: "nextPageToken, files(id,name,mimeType,iconLink,webViewLink,shortcutDetails)",
      pageSize: 200,
      q: buildMimeTypeQuery(app),
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
      corpora: "user",
      spaces: "drive",
    };

    let all = [];
    let pageToken = undefined;

    do {
      const { data } = await axios.get("https://www.googleapis.com/drive/v3/files", {
        params: { ...paramsBase, pageToken },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const items = await Promise.all(
        (data.files || []).map(async (file) => {
          // resolve shortcuts to their targets (keeps your old behavior)
          if (file.shortcutDetails?.targetId) {
            const { data: tgt } = await axios.get(
              `https://www.googleapis.com/drive/v3/files/${file.shortcutDetails.targetId}`,
              {
                params: {
                  fields: "id,name,mimeType,iconLink,webViewLink",
                  supportsAllDrives: true,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );
            return tgt;
          }
          return file;
        })
      );

      all = all.concat(items);
      pageToken = data.nextPageToken;
    } while (pageToken);

    setAccessibleItems(all);
  } catch (e) {
    console.error("Drive list error:", e);
    setAccessibleItems([]);
  } finally {
    setItemsLoading(false);
  }
};

  
// store selections back into your node
const saveSelections = (ids) => {
  // 1) persist in the node (your real source of truth)
  saveNode({
    ...node,
    data: { ...node.data, pickerSelections: ids },
  });
  // 2) persist locally so chips survive remounts / timing issues
  writeSelections(ids);
  // 3) update UI immediately
  // (we still call resolvePickedItems below to fetch names/icons)
};

  const spanRef = useRef();

  const uniq = (arr) => [...new Set(arr)];

  // 1) Load credentials (tenant-wide + user-scoped, label + slug)
  const handleLoad = async () => {
    if ((!tenant && !user) || (!applabel && !app)) return;
    try {
      const list = await fetchAllCredentialsForApp({
        tenant,
        user,
        appSlug: app,
        appLabel: applabel,
      });
      setAuths(list);
    } catch (error) {
      console.error(error);
    }
  };

  // 2) On mount or when tenant/app changes, load
  useEffect(() => {
    handleLoad();
  }, [tenant, applabel, app, user]);

  // 3) If no credential is stored, pick the lastUsed or last one
  useEffect(() => {
    if (auths.length > 0 && !node.data.authname) {
      const withTimestamp = auths.filter((c) => c.lastUsed);
      let selectedCredential;
      if (withTimestamp.length > 0) {
        // pick the one with the highest lastUsed
        selectedCredential = withTimestamp.reduce((prev, current) =>
          prev.lastUsed > current.lastUsed ? prev : current
        );
      } else {
        selectedCredential = auths[auths.length - 1];
      }
      if (selectedCredential) {
        const i = auths.indexOf(selectedCredential);
        const displayValue = `${selectedCredential.app} - ${selectedCredential.name ?? 'unnamed'}#${i + 1}`;
        setAuthName(displayValue);
        sendAuth(displayValue);
        sendData(selectedCredential._id.$oid);
        updateLastUsed(selectedCredential._id.$oid);
        testCredential(selectedCredential._id.$oid);
      }
    } else {
      if (node.data.authname && node.data.credential && !credentialStatus) {
        testCredential(node.data.credential);
      }
      
    }
  }, [auths, node.data.authname]);

  // 4) Update lastUsed
  const updateLastUsed = async (credentialId) => {
    const currentTimestamp = Date.now();
    try {
      await axios.patch(
        `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?id=${credentialId}&key=lastUsed&value=${currentTimestamp}`
      );
    } catch (error) {
      console.error('Error updating lastUsed:', error);
    }
  };

  // 5) Test credential placeholder
  const testCredential = async (credentialId) => {
    let lambdaURL = null
    let testAction = null
    try {
      // placeholder test; assume success
      // If real test fails, set to 'failure'
      setTestLoading(true)
      
      // Ensure app is defined and is a string
      if (!app || typeof app !== 'string') {
        console.error('Authorizer: Invalid app value:', app);
        setCredentialStatus('failure');
        setTestLoading(false);
        return;
      }
      
      axios({
        headers: {
            'content-type': 'application/json'
        },
        method: 'get',
        url: 'https://editor.workload.co:5000/api/apps/' + app  
    }).then(resp => {
      // Check if resp.data exists before accessing properties
      if (!resp || !resp.data) {
        console.error('Authorizer: No data returned from API for app:', app);
        setCredentialStatus('failure');
        setTestLoading(false);
        return;
      }
      
      if (resp.data.lambdaURL !== "") {
        lambdaURL = resp.data.lambdaURL
    }
    else {
        lambdaURL = "https://odkusrnpx8.execute-api.us-west-2.amazonaws.com/production/apps/"+app
    }
      const authInfo = resp.data.authInfo
      
      // Check if authInfo exists
      if (!authInfo) {
        console.error('Authorizer: No authInfo in response for app:', app);
        setCredentialStatus('failure');
        setTestLoading(false);
        return;
      }
      if (resp.data.disableAuth === true) {
        setCredentialStatus('success');
        setTestLoading(false)
        return
      }
      // Phase D Browser RPA: credential is username/password vault only — skip HTTP testAuth.
      const isBrowserLogin =
        authInfo.browserLogin === true ||
        authInfo.browser_login === true ||
        resp.data.browserRpa === true ||
        String(resp.data.automationMode || '').toLowerCase() === 'browser_rpa';
      if (isBrowserLogin) {
        setCredentialStatus('success');
        setTestLoading(false);
        return;
      }
      else if (authInfo.apiType === 'oauth2') {
        if (Object.entries(resp.data.actions).filter(([key,value]) =>
            value.method === 'get' &&
            Array.isArray(value.fields) && value.fields.length === 0 &&
            value.url)[0]) {
                testAction = Object.entries(resp.data.actions).filter(([key,value]) =>
                value.method === 'get' &&
                Array.isArray(value.fields) && value.fields.length === 0 &&
                value.url)[0][0]
            }
                else {
                  setCredentialStatus('success');
                  setTestLoading(false)
                  return
                }
      } else {
        testAction = authInfo.testAuth
      }
      var testAuth = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            "operation": "testAuth",
            "data": {
                "url": lambdaURL,
                "app": app,
                "credential": credentialId,
                "action": testAction,
                "fields": []
            }
        }
    };
    axios(testAuth)
          .then(function (response) {
            setCredentialStatus('success')
            setTestLoading(false)
        }).catch(function (error) {
          console.error('Authorizer: Error testing credential:', error);
          setCredentialStatus('failure');
        setTestLoading(false)
        })
      ;})
    } catch (error) {
      setCredentialStatus('failure');
      setTestLoading(false)
    }
  };

  // 6) Handling new credentials from the popup
  const handleNewAuth = async (credentialId, displayValue) => {
    try {
      const list = await fetchAllCredentialsForApp({
        tenant,
        user,
        appSlug: app,
        appLabel: applabel,
      });
      setAuths(list);
      setNewAuthId(credentialId);
      const found = list.find((c) => (c._id?.$oid ?? c._id) === credentialId);

      if (!found?.name) {
        setNameChange('');
        setOpenModalState(true);
      } else {
        const i = list.indexOf(found);
        const showName = `${found.app} - ${found.name ?? 'unnamed'}#${i + 1}`;
        setAuthName(showName);
        sendAuth(showName);
        sendData(credentialId);
        updateLastUsed(credentialId);
        testCredential(credentialId);
        if (isGoogleApp(app)) {
          listFilesForApp(credentialId, app);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 7) The popup logic
  let windowObjectReference = null;
  let previousUrl = null;

  const openGoogleAuthPopup = (url, name) => {
    const ALLOWED_ORIGINS = [
      'https://editor.workload.co',
      'https://app.workload.co',
      'https://editor.workload.co:5000',
      'http://localhost:3000',
      'http://localhost:5000',
    ];
    
    const receiveGoogleAuth = (event) => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) return;
      const credId = typeof event.data === 'string' ? event.data : event.data?.credentialId;
      if (!credId) return;
    
      handleNewAuth(credId, `${applabel} Account`);
      openPickerForGoogleApp(app, credId);        // 🚀 auto-open after connect
      window.removeEventListener('message', receiveGoogleAuth);
      popup?.close();
    };
    window.addEventListener('message', receiveGoogleAuth);

    const strWindowFeatures = 'toolbar=no, menubar=no, width=600, height=700, top=100, left=100';
    const popup = window.open(url, name, strWindowFeatures);
    
  };
  
  

  const addAuth = (url, name) => {
    if (!user) {
      sendLoggedOutToAuthorizer(true);
      return;
    }

    // ✅ If it's a Google app, route through the special picker flow
  if (GOOGLE_APPS.includes(app)) {
    openGoogleAuthPopup(url, name);
    return;
  }

    window.removeEventListener('message', receiveMessage);
    const strWindowFeatures = 'toolbar=no, menubar=no, width=600, height=700, top=100, left=100';

    if (windowObjectReference === null || windowObjectReference.closed) {
      windowObjectReference = window.open(url, name, strWindowFeatures);
    } else if (previousUrl !== url) {
      windowObjectReference = window.open(url, name, strWindowFeatures);
      windowObjectReference.focus();
    } else {
      windowObjectReference.focus();
    }
    window.addEventListener('message', receiveMessage, false);
    previousUrl = url;
  };

  const receiveMessage = (event) => {
    const { data } = event;
    // data is the newly created credential's _id.$oid
    sendData(data);
    // fetch that single credential
    axios
      .get(
        `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?tenant=${tenant}&id=${data}`
      )
      .then((response) => {
        // example: response.data[0] is the newly created credential
        if (!response.data || !response.data[0]) return;
        const displayValue = `${response.data[0].app} - ${response.data[0].name}`;
        handleNewAuth(data, displayValue);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const openPickerForGoogleApp = async (app, credentialId) => {
    try {
      // Load gapi and picker if not already loaded
      if (!window.gapi) {
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://apis.google.com/js/api.js';
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }
  
      if (!window.google?.picker) {
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://apis.google.com/js/picker.js';
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }
  
      // Get OAuth token for this credential
      const tokenRes = await getFreshAccessToken(credentialId);
      const accessToken = tokenRes;
  
      const picker = new window.google.picker.PickerBuilder()
        .setOAuthToken(accessToken)
        .setDeveloperKey('AIzaSyA8T47mgCZbzbUfipstiMoV8MzIeFvuPGM') // replace with your key
        .setAppId('190213605534')             // 👈 IMPORTANT: numeric project number
        .setCallback((data) => handlePickerSelection(data, app, credentialId))
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES) // 👈 important
        .setSize(window.innerWidth * 0.8, window.innerHeight * 0.8)
        .setTitle('Choose folders/files to share with Workload')
       // .build().setVisible(true);
  
      // Pick the right DocsView or FolderView based on app
      const mimeTypes = MIME_TYPES_BY_APP[app] || [];
  
      const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true);
  
      if (mimeTypes.length > 0) {
        view.setMimeTypes(mimeTypes.join(','));
      }
  
      picker.addView(view);
      picker.build().setVisible(true);
  
    } catch (err) {
      console.error('Error opening picker', err);
    }
  };
  const selKey = node?.id ? `pickerSelections:${node.id}` : null;

const readSelections = () => {
  const fromNode = getSelections(node);
  if (fromNode?.length) return fromNode;
  if (!selKey) return [];
  try { return JSON.parse(sessionStorage.getItem(selKey) || '[]'); }
  catch { return []; }
};

const writeSelections = (ids=[]) => {
  if (selKey) sessionStorage.setItem(selKey, JSON.stringify(ids));
};

const handlePickerSelection = (data, app, credentialId) => {
  const action = data[window.google.picker.Response.ACTION];
  if (action !== window.google.picker.Action.PICKED) return;

  // We don't need to store IDs anymore just to show chips.
  // Selecting via Picker grants the app drive.file access to those items.
  // Simply re-list what the app can access now:
  listFilesForApp(credentialId, app);
};


const removeSelection = (id) => {
  const nextIds = readSelections().filter(x => x !== id);
  saveSelections(nextIds);
  if (node?.data?.credential) resolvePickedItems(node.data.credential, nextIds);
};

  
  const resolvePickedItems = async (credentialId, ids=[]) => {
    if (!ids.length) { setPickedItems([]); return; }
    const tokenRes    = await getFreshAccessToken(credentialId);;
    const accessToken = tokenRes;
  
    const results = await Promise.all(ids.map(async (id) => {
      try {
        const r = await axios.get(`https://www.googleapis.com/drive/v3/files/${id}`, {
          params: {
            fields: 'id,name,mimeType,iconLink,webViewLink,shortcutDetails',
            supportsAllDrives: true,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const file = r.data;
        // If it's a shortcut, optionally resolve its target
        if (file.shortcutDetails?.targetId) {
          const rr = await axios.get(`https://www.googleapis.com/drive/v3/files/${file.shortcutDetails.targetId}`, {
            params: {
              fields: 'id,name,mimeType,iconLink,webViewLink',
              supportsAllDrives: true,
            },
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          return rr.data;
        }
        return file;
      } catch { return null; }
    }));
  
    setPickedItems(results.filter(Boolean));
  };
  
  
  // Refresh the "shared with Workload" list on mount/changes
useEffect(() => {
  if (!isGoogleApp(app)) return;
  if (!node?.data?.credential) return;
  listFilesForApp(node.data.credential, app);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [app, node?.data?.credential]);

  
  
/*   useEffect(() => {
  }, [node]); */
  
  

  // 8) Deleting
  const doDelete = async (credentialId) => {
    try {
      await axios.delete(
        `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?id=${credentialId}`
      );
      // Re-load the credentials from the server
      handleLoad();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteConnection = (credentialId) => {
    if (reallyDelete === true && typeof credentialId === 'string') {
      setDeleting(true);
      doDelete(credentialId);
      setAuthName('');
      saveNode(node);
      setDeleting(false);
      setReallyDelete(false);
      handleMoreClose();
    }
  };

  // 9) Editing the name
  const handleOpenModal = () => {
    setOpenModalState(true);
  };
  const handleCloseModal = () => {
    setOpenModalState(false);
  };
  const handleNameChange = (val) => {
    setNameChange(val);
  };

  const doSubmit = async () => {
    try {
      // if we just created a brand new credential, use that ID
      const credIdToUpdate = newAuthId || node.data.credential;
      if (!credIdToUpdate) {
        handleCloseModal();
        return;
      }
      await axios.patch(
        `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?id=${credIdToUpdate}&key=name&value=${namechange}`
      );
      setOpenModalState(false);
      handleLoad(); // reload all credentials
      // Then set the updated name as the "authName"
      // We can do a quick GET to fetch the updated credential:
      const updatedRes = await axios.get(
        `https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/credentials?tenant=${tenant}&id=${credIdToUpdate}`
      );
      if (updatedRes.data && updatedRes.data[0]) {
        const c = updatedRes.data[0];
        // find its index in the array for the # index
        const indexInAuths = auths.findIndex((x) => x._id.$oid === credIdToUpdate);
        const displayName = `${c.app} - ${c.name ?? 'unnamed'}#${indexInAuths + 1}`;
        setAuthName(displayName);
        sendAuth(displayName);
        sendData(credIdToUpdate);
        updateLastUsed(credIdToUpdate);
        testCredential(credIdToUpdate);

      }
      setNewAuthId(null);
    } catch (error) {
      console.error(error);
    }
  };
  function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  }
  
  function formatTimestamp(ts) {
    const date = new Date(ts);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${month} ${day}${suffix}, ${year} ${time}`;
  }
  
  // 10) Menus for "Change" and "More"
  const [anchorElChange, setAnchorElChange] = useState(null);
  const [anchorElMore, setAnchorElMore] = useState(null);

  const handleChooseClick = () => {
    setAnchorElChange(spanRef.current);
  };
  const handleChooseClose = () => {
    setAnchorElChange(null);
  };
  const handleMoreClick = () => {
    setAnchorElMore(spanRef.current);
  };
  const handleMoreClose = () => {
    setAnchorElMore(null);
  };

  const StyledMenu = withStyles({
    paper: {
      border: '1px solid #d3d4d5',
      width: spanRef.current ? spanRef.current.offsetWidth : 583,
      marginTop: '8px',
    },
  })((props) => (
    <Menu
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      {...props}
    />
  ));

  // 11) The main UI
  return (
    <div>
      {/* If no credentials exist */}
      {auths.length === 0 ? (
        <div>
          <br />
          <div
            style={{
              display: 'flex',
              border: '1px solid silver',
              borderRadius: '5px',
              padding: '12px',
              marginBottom: '12px',
            }}
          >
            <div
              style={{
                float: 'left',
                display: 'flex',
                alignItems: 'center',
                marginRight: 'auto',
                marginTop: '4px',
              }}
            >
              <Avatar
                style={{
                  width: breakpoint === 'small' ? '24px' : null,
                  height: breakpoint === 'small' ? '24px' : null,
                }}
                src={icon}
              />
              <Typography
                variant="h6"
                style={{
                  marginLeft: '18px',
                  fontWeight: 'bold',
                  fontSize: breakpoint !== 'large' ? '16px' : null,
                }}
              >
                Connect {applabel}
              </Typography>
            </div>
            {custombutton ? (
              <div style={{ flex: 1, alignItems: 'center', cursor: 'pointer' }}>
                <img
                  style={{ marginLeft: '6px', width: '100%' }}
                  src={custombutton}
                  onClick={() =>
                    addAuth(`https://editor.workload.co:5000/auth/${app}?uid=${user}&t=${tenant}`, 'authWindow')
                  }
                />
              </div>
            ) : (
              <button
                size="small"
                id="new-auth-button"
                className={classes.button2}
                color="primary"
                variant="contained"
                onClick={() =>
                  addAuth(`https://editor.workload.co:5000/auth/${app}?uid=${user}&t=${tenant}`, 'authWindow')
                }
              >
                <Typography
                  variant="h6"
                  style={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: breakpoint !== 'large' ? '16px' : null,
                  }}
                >
                  Sign in
                </Typography>
              </button>
            )}
          </div>
          <SecurityRoundedIcon style={{ color: 'gray', height: '18px', float: 'left', display: 'block' }} />
          <Typography style={{ fontSize: '14px', color: 'gray' }}>
            {applabel} is a secure partner with Workload.{' '}
            <a style={{ color: '#0069ea' }} href="https://workload.co/privacy" target="_blank" rel="noreferrer">
              Your credentials are encrypted &amp; can be removed at any time
            </a>
            .{' '}
            <a style={{ color: '#0069ea' }} href="https://automate.workload.co/connection" target="_blank" rel="noreferrer">
              Manage your connected accounts here
            </a>
            .
          </Typography>
          {isDocuSignApp(app, applabel) && (
            <DocuSignDigitalReferralButton style={{ display: 'block', marginTop: 12 }} />
          )}
        </div>
      ) : (
        // If credentials exist
        <>
          <Typography>
            <strong>{applabel} account:</strong> (required)
          </Typography>

          {/* Show success/failure badge if we have a credentialStatus */}
          {/* {credentialStatus === 'success' && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', gap: '8px' }}>
              <Badge classes={{ badge: classes.badgeSuccess }} variant="dot" overlap="circle">
                <span style={{ width: '12px', height: '12px' }} />
              </Badge>
              <Typography variant="subtitle2" style={{ color: '#4caf50' }}>
                Connection successful
              </Typography>
            </div>
          )} */}
          {credentialStatus === 'failure' && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', gap: '8px' }}>
              <Badge classes={{ badge: classes.badgeFailure }} variant="dot" overlap="circle">
                <span style={{ width: '12px', height: '12px' }} />
              </Badge>
              <Typography variant="subtitle2" style={{ color: '#f44336' }}>
                Connection expired, please reconnect an account
              </Typography>
            </div>
          )}

          <div style={{ marginTop: '12px' }}>
            <div
              ref={spanRef}
              style={{
                display: 'flex',
                border: '1px solid silver',
                borderRadius: '5px',
                padding: '12px',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}>
                <Avatar
                  style={{
                    width: breakpoint === 'small' ? '24px' : null,
                    height: breakpoint === 'small' ? '24px' : null,
                  }}
                  src={icon}
                />
                <Typography
                  variant="h6"
                  style={{
                    marginLeft: '24px',
                    fontWeight: 'bold',
                    fontSize: breakpoint !== 'large' ? '16px' : null,
                  }}
                >
                  {authname
                    ? authname.split('#')[0] === `${applabel} - undefined`
                      ? `${applabel} - unnamed`
                      : authname.split('#')[0]
                    : `Connect ${applabel}`}
                </Typography>
              </div>
              <button
                size="small"
                id="change-auth-button"
                className={classes.button2}
                color="primary"
                variant="contained"
                onClick={() => setAnchorElChange(spanRef.current)}
              >
                <Typography
                  variant="h6"
                  style={{ color: 'white', fontWeight: 'bold', fontSize: breakpoint !== 'large' ? '16px' : null }}
                >
                  {authname ? 'Change' : 'Choose'}
                </Typography>
              </button>
              {authname && (
                <IconButton onClick={() => setAnchorElMore(spanRef.current)} style={{ float: 'right', color: 'black' }}>
                  <MoreHorizRoundedIcon />
                </IconButton>
              )}
            </div>
            {isGoogleApp(app) && (
  <div style={{ border: '1px dashed #cbd5e1', borderRadius: 6, padding: 12, marginTop: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 12 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
        Files/folders shared with Workload
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={() => openPickerForGoogleApp(app, node.data.credential)}
      >
        {accessibleItems.length ? 'Change' : 'Choose'}
      </Button>
      {itemsLoading && <CircularProgress size={18} />}
    </div>

    {!itemsLoading && accessibleItems.length === 0 ? (
      <Typography variant="body2" style={{ color: '#64748b' }}>
        No files or folders yet. Click **Choose** to select items with the Google Picker.
      </Typography>
    ) : (
      <div  style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        maxHeight: 220,        // 👈 set your preferred height (px)
        overflowY: 'auto',     // 👈 scroll when overflow
        paddingRight: 4        // avoids scrollbar overlaying chips
      }}>
        {accessibleItems.map(item => (
          <span
            key={item.id}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              border: '1px solid #e2e8f0', borderRadius: 18, padding: '6px 10px'
            }}
            title={item.name || item.id}
          >
            {item.iconLink && <img src={item.iconLink} alt="" width={16} height={16} />}
            <a
              href={item.webViewLink || '#'}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', color: 'gray' }}
            >
              {item.name || item.id}
            </a>
          </span>
        ))}
      </div>
    )}
  </div>
)}


            {/* "Change" dropdown */}
            <StyledMenu
              id="change-menu"
              anchorEl={anchorElChange}
              keepMounted
              open={Boolean(anchorElChange)}
              onClose={() => setAnchorElChange(null)}
            >
              <Typography style={{ padding: '10px', fontWeight: 700 }} variant="h6">
                Choose...
              </Typography>
              <Divider style={{ marginBottom: '6px' }} />
              {auths.length > 0
                ? auths.map((item, i) => {
                    const displayVal = `${item.app} - ${item.name ?? 'unnamed'}`;
                    const displayCreatedAt = item.createdAt ? "Created: "+ formatTimestamp(item.createdAt) : null
                    return (
                      <MenuItem
                        key={item._id.$oid}
                        onClick={() => {
                          setAuthName(displayVal);
                          setAnchorElChange(null);
                          updateLastUsed(item._id.$oid);
                          testCredential(item._id.$oid);
                          sendAuth(displayVal);
                          sendData(item._id.$oid);
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            style={{
                              width: breakpoint === 'small' ? '18px' : '26px',
                              height: breakpoint === 'small' ? '18px' : '26px',
                            }}
                            src={icon}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={displayVal}
                          secondary={displayCreatedAt}
                          primaryTypographyProps={{
                            style: { fontWeight: 'bold', fontSize: '20px', marginLeft: '0px' },
                          }}
                        />
                      </MenuItem>
                    );
                  })
                : (
                  <MenuItem disabled>+ Add a new connection</MenuItem>
                )}
              <Divider />
              <div style={{ margin: '8px' }}>
                <Button
                  size="small"
                  id="new-auth-button"
                  className={classes.button3}
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    setAnchorElChange(null);
                    addAuth(`https://editor.workload.co:5000/auth/${app}?uid=${user}&t=${tenant}`, 'authWindow');
                  }}
                >
                  <Typography style={{ color: '#0069ea', fontSize: breakpoint !== 'large' ? '12px' : null }}>
                    + Add new {applabel} account
                  </Typography>
                </Button>
              </div>
            </StyledMenu>

            {/* "More" dropdown */}
            <StyledMenu
              id="more-menu"
              anchorEl={anchorElMore}
              keepMounted
              open={Boolean(anchorElMore)}
              onClose={() => setAnchorElMore(null)}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              style={{width: '300px'}}
            >
              <MenuItem
                onClick={() => {
                  setAnchorElMore(null);
                  setOpenModalState(true);
                }}
              >
                <ListItemIcon>
                  <EditRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Edit connection name" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorElMore(null);
                  addAuth(
                    `https://editor.workload.co:5000/auth/${app}?uid=${user}&t=${tenant}&update=true&c_id=${node.data.credential}`,
                    'authWindow'
                  );
                }}
              >
                <ListItemIcon>
                  <DescriptionRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Update connection info" />
              </MenuItem>
              {reallyDelete ? (
                <MenuItem
                  onClick={() => {
                    setAnchorElMore(null);
                    handleDeleteConnection(node.data.credential);
                  }}
                >
                  {deleting ? (
                    <CircularProgress size={20} color="secondary" style={{ margin: '0 auto' }} />
                  ) : (
                    <ListItemIcon>
                      <DeleteRoundedIcon fontSize="small" style={{ color: 'red' }} />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={deleting ? '' : 'Really delete?'} />
                </MenuItem>
              ) : (
                <MenuItem onClick={() => setReallyDelete(true)}>
                  <ListItemIcon>
                    <DeleteRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Delete connection" />
                </MenuItem>
              )}
            </StyledMenu>
          </div>
          <FormHelperText>
            <SecurityRoundedIcon style={{ color: 'gray', height: '15px', float: 'left', display: 'block' }} />
            <Typography style={{ fontSize: '14px', color: 'gray' }}>
              {applabel} is a secure partner with Workload.{' '}
              <a style={{ color: '#0069ea' }} href="https://workload.co/privacy" target="_blank" rel="noreferrer">
                Your credentials are encrypted &amp; can be removed at any time
              </a>
              .{' '}
              <a style={{ color: '#0069ea' }} href="https://automate.workload.co/connection" target="_blank" rel="noreferrer">
                Manage your connected accounts here
              </a>
              .
            </Typography>
          </FormHelperText>
        </>
      )}

      <Modal open={openModalState} onClose={handleCloseModal}>
        <div style={modalStyle} className={classes.paper}>
          <h2>Edit connection name</h2>
          <TextField
            style={{ width: '100%' }}
            required
            variant="outlined"
            label="Name (required)"
            value={namechange}
            onChange={(e) => handleNameChange(e.target.value)}
            helperText="Enter a name that will help you remember which account this connection belongs to."
          />
          <Button
            variant="contained"
            color="primary"
            style={{ width: '100%', marginTop: '16px' }}
            onClick={doSubmit}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
}
